<template>
  <v-dialog
    :value="value"
    max-width="650px"
    @input="close">
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('heading') }}</span>
      </v-card-title>
      <v-card-text>

        <v-form
          ref="employeeEditForm"
          v-model="employeeEditForm">

          <p
            v-for="(error, index) in logicErrors"
            :key="index"
            class="mx-1 error--text"
          >
            {{ error }}
          </p>

          <date-picker
            v-model="inactivityObj.begin"
            :label="$t('begin') + '*'"
            :rules="[$rules.isRequired]"
            :error-messages="errorMessages.begin"/>

          <date-picker
            v-model="inactivityObj.end"
            :label="$t('end')"
            :allowed-dates="allowedEndDates"
            :error-messages="errorMessages.end"/>

          <v-text-field
            v-model="inactivityObj.comment"
            :label="$t('comment') + '*'"
            :rules="[$rules.isRequired]"
            :error-messages="errorMessages.comment"/>

        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          flat
          @click="close">{{ $t('cancel') }}
        </v-btn>
        <disabled-user-button>
          <v-btn
            slot-scope="{isDisabledForUser}"
            :disabled="!employeeEditForm || isDisabledForUser || isWaitingForServerResponse"
            :loading="isWaitingForServerResponse"
            color="primary"
            @click="saveEntry"
          >
            {{ $t('save') }}
          </v-btn>
        </disabled-user-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import CompanyApi from '@/api/CompanyApi'
import MonthPicker from '@/components/global-components/inputs/MonthPicker'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import DatePicker from '@/components/global-components/inputs/DatePicker'

export default {
  name: 'EmployeeInactivtyEntryDialog',
  components: { DatePicker, DisabledUserButton, MonthPicker },
  props: ['value'],
  data () {
    return {
      inactivityObj: {
        begin: null,
        end: null,
        comment: ''
      },
      isWaitingForServerResponse: false,
      employeeEditForm: true,
      isSnackbar: true,
      logicErrors: [],
      errorMessages: {}
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    firstDayOfBegin () {
      if (this.inactivityObj.begin) {
        return this.$moment(this.inactivityObj.begin, 'YYYY-MM-DD')
      }
    },
    lastDayOfEnd () {
      if (this.inactivityObj.end) {
        return this.$moment(this.inactivityObj.end, 'YYYY-MM-DD')
      }
    }
  },
  methods: {
    createEntryByRole (inactivityObj) {
      return CompanyApi.setEmployeeInactivityPeriod(this.currentEmployee.id, inactivityObj)
    },

    close () {
      this.$emit('input', false)
      this.resetInitialData()
    },

    resetInitialData () {
      this.$refs.employeeEditForm.reset()

      this.inactivityObj = {
        end: null,
        begin: null,
        comment: ''
      }

      this.errorMessages = {}
      this.logicErrors = []
    },

    async saveEntry () {
      const begin = this.firstDayOfBegin.format('DD-MM-YYYY')
      const end = this.lastDayOfEnd ? this.lastDayOfEnd.format('DD-MM-YYYY') : null
      const comment = this.inactivityObj.comment

      const inactivityObj = { begin, end, comment }

      this.isWaitingForServerResponse = true

      try {
        await this.createEntryByRole(inactivityObj)
        this.resetInitialData()
        await this.$emit('updateOnSuccess')
        this.close()
      } catch (e) {
        if (e.errors.message) {
          this.logicErrors = e.errors.message
        } else {
          this.errorMessages = e.errors
        }
      }

      this.isWaitingForServerResponse = false
    },

    allowedEndDates (val) {
      const $date = this.$moment(val, 'YYYY-MM-DD')
      let isAfterBegin = true

      if (this.inactivityObj.begin) {
        isAfterBegin = this.firstDayOfBegin.diff($date, 'days') < 0
      }
      return isAfterBegin
    }
  },
  i18n: {
    messages: {
      en: {
        begin: 'From day (incl.)',
        cancel: 'Cancel',
        comment: 'Comment',
        end: 'To day (incl.)',
        heading: 'Add inactivity',
        isRequired: 'Required field',
        save: 'Save'
      },
      tr: {
        begin: 'İlk gün ',
        cancel: 'İptal',
        comment: 'Yorum',
        end: 'Son gün',
        heading: 'Pasif ekle',
        isRequired: 'Zorunlu alan',
        save: 'Kaydet'
      },
      de: {
        begin: 'Erster Tag (inkl.)',
        cancel: 'Abbrechen',
        comment: 'Kommentar',
        end: 'Letzter Tag (inkl.)',
        heading: 'Inaktivität hinzufügen',
        isRequired: 'Pflichtfeld',
        save: 'Speichern'
      },
      ru: {
        begin: 'Со дня (включая)',
        cancel: 'Отмена',
        comment: 'Комментарий',
        end: 'Ко дню (в том числе)',
        heading: 'Добавить период неактивности',
        isRequired: 'Обязательное поле',
        save: 'Сохранить'
      }
    }
  },
  watch: {
    inactivityObj: {
      deep: true,
      handler () { this.errorMessages = {} }
    }
  }
}
</script>
